// src/services/userService.ts
import { apiClient } from '../api/apiClient';

interface UpdateProfileData {
  name?: string;
  phone?: string;
  email?: string;
  bio?: string;
  specialties?: string[];
}


interface NotificationPreferences {
  email: {
    appointments: boolean;
    marketing: boolean;
    reminders: boolean;
  };
  sms: {
    appointments: boolean;
    reminders: boolean;
  };
}

export const userService = {
  // Get current user profile
  async getCurrentUser() {
    const response = await apiClient.get('/profile/me');
    return response.data.data;
  },
  
  // Update profile information (matches mobile: PUT /profile/update)
  async updateProfile(data: UpdateProfileData) {
    // Remove empty phone numbers to avoid validation errors
    const cleanData: any = { ...data };
    
    // Handle both 'phone' and 'phoneNumber' fields
    if (cleanData.phone === '' || cleanData.phone === null || cleanData.phone === undefined) {
      delete cleanData.phone;
    }
    if (cleanData.phoneNumber === '' || cleanData.phoneNumber === null || cleanData.phoneNumber === undefined) {
      delete cleanData.phoneNumber;
    }
    
    const response = await apiClient.put('/profile/update', cleanData);
    return response.data.data;
  },
  
  // Update profile photo (matches mobile: POST /profile/photo)
  async updateProfilePhoto(photoData: FormData) {
    const response = await apiClient.post('/profile/photo', photoData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data.data;
  },
  
  // Delete profile photo
  async deleteProfilePhoto() {
    const response = await apiClient.delete('/profile/photo');
    return response.data.data;
  },
  
  // Update user password (matches mobile: PUT /auth/update-password)
  async updatePassword(data: { currentPassword: string; newPassword: string }) {
    const response = await apiClient.put('/auth/update-password', data);
    return response.data.data;
  },
  
  // Update notification settings via profile preferences
  async updateSecuritySettings(settings: { [key: string]: boolean }) {
    const notifications: { email?: boolean; push?: boolean } = {};

    if ('emailNotifications' in settings) {
      notifications.email = settings.emailNotifications;
    }
    if ('pushNotifications' in settings) {
      notifications.push = settings.pushNotifications;
    }

    const response = await apiClient.put('/profile/preferences', { notifications });
    return response.data;
  },
  
  // Delete account — schedule deletion (30-day grace). Matches POST /account/delete-request
  async deleteAccount(password: string, reason = 'User requested deletion') {
    const response = await apiClient.post('/account/delete-request', {
      password,
      reason,
    });
    return response.data;
  },

  // Cancel a scheduled account deletion during the grace period
  async cancelAccountDeletion() {
    const response = await apiClient.post('/account/cancel-deletion');
    return response.data;
  },

  // Export GDPR data archive (requires password reauth)
  async exportAccountData(password: string) {
    const response = await apiClient.post(
      '/account/export',
      { password },
      { responseType: 'blob' }
    );
    return response.data;
  },
  
  // Get client stats (appointments, loyalty, etc.)
  async getClientStats() {
    const response = await apiClient.get('/clients/stats');
    return response.data.data;
  },
  
  // Get stylist stats (appointments, earnings, etc.)
  async getStylistStats() {
    const response = await apiClient.get('/stylists/stats');
    return response.data.data;
  },
  
  // Get notification preferences
  async getNotificationPreferences() {
    const response = await apiClient.get('/users/notifications/preferences');
    return response.data.data;
  },
  
  // Update notification preferences
  async updateNotificationPreferences(preferences: NotificationPreferences) {
    const response = await apiClient.patch('/users/notifications/preferences', preferences);
    return response.data.data;
  },
  
  // Get saved payment methods (for clients)
  async getSavedPaymentMethods() {
    const response = await apiClient.get('/users/payment-methods');
    return response.data.data;
  },
  
  // Delete a saved payment method
  async deletePaymentMethod(paymentMethodId: string) {
    const response = await apiClient.delete(`/users/payment-methods/${paymentMethodId}`);
    return response.data.data;
  },
  
  // Set a payment method as default
  async setDefaultPaymentMethod(paymentMethodId: string) {
    const response = await apiClient.patch(`/users/payment-methods/${paymentMethodId}/default`);
    return response.data.data;
  },

  // Admin/Support: Search users (for support dashboard)
  async searchUsers(searchQuery: string, filters?: { userType?: string; limit?: number }) {
    const params: Record<string, string | number> = {};
    const q = searchQuery?.trim();
    if (q) params.search = q;
    if (filters?.userType) params.userType = filters.userType;
    if (filters?.limit) params.limit = filters.limit;

    const response = await apiClient.get('/users', { params });
    return response.data.data;
  },

  // Admin/Support: Get user by ID with full details
  async getUserById(userId: string) {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data.data;
  },

  // Admin/Support: Verify user email
  async verifyUserEmail(userId: string) {
    const response = await apiClient.put(`/users/${userId}/verify-email`);
    return response.data.data;
  }
};

export default userService;